import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, CalendarClock, Wrench } from "lucide-react";
import { useApi } from "../lib/api";
import { PageHead, Card, Btn, Loading, StatusBadge } from "../components/kit";
import { todayWIB, fmtDate, num, DONE } from "../lib/constants";

const MONTHS = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const DAYS = ["Sen", "Sel", "Rab", "Kam", "Jum", "Sab", "Min"];
const pad = (n) => String(n).padStart(2, "0");

function buildGrid(ym) {
  const [y, m] = ym.split("-").map(Number);
  const first = new Date(y, m - 1, 1);
  const lead = (first.getDay() + 6) % 7;
  const days = new Date(y, m, 0).getDate();
  const cells = Array.from({ length: lead }, () => null);
  for (let d = 1; d <= days; d++) cells.push(`${ym}-${pad(d)}`);
  while (cells.length % 7) cells.push(null);
  return cells;
}

function shiftMonth(ym, delta) {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
}

const TONE = {
  late: { card: "border-rose-400 bg-rose-50", badge: "bg-rose-600", text: "text-rose-700", label: "Telat" },
  deadline: { card: "border-amber-300 bg-amber-50", badge: "bg-amber-500", text: "text-amber-800", label: "Deadline" },
  schedule: { card: "border-sky-300 bg-sky-50", badge: "bg-sky-500", text: "text-sky-800", label: "Produksi" },
};
const isLate = (e, date, today) => e.kind === "deadline" && date < today && !DONE.includes(e.o.status);
const toneOf = (e, date, today) => TONE[isLate(e, date, today) ? "late" : e.kind];

function dayNumberCls(date, today, late) {
  if (date === today) return "bg-slate-900 text-white";
  return late ? "text-rose-600" : "text-slate-700";
}

function DayCell({ date, events, today, selected, onSelect }) {
  if (!date) return <div className="min-h-[72px] sm:min-h-[96px] bg-slate-50/60" />;
  const dl = events.filter((e) => e.kind === "deadline"), sc = events.filter((e) => e.kind === "schedule");
  const late = dl.some((e) => isLate(e, date, today));
  return (
    <button type="button" onClick={() => onSelect(date)} data-testid={`cal-day-${date}`}
      className={`min-h-[72px] sm:min-h-[96px] p-1.5 text-left bg-white border-t border-l border-slate-200 flex flex-col gap-1 transition-colors hover:bg-sky-50 ${selected === date ? "ring-2 ring-inset ring-sky-500" : ""}`}>
      <span className={`self-start w-7 h-7 grid place-items-center rounded-full text-sm font-bold ${dayNumberCls(date, today, late)}`}>{Number(date.slice(8))}</span>
      <div className="flex flex-wrap gap-1 mt-auto">
        {dl.length > 0 && <span className={`px-1.5 h-5 inline-flex items-center gap-0.5 rounded text-[11px] font-black text-white ${late ? "bg-rose-600" : "bg-amber-500"}`} title="Deadline"><CalendarClock className="w-3 h-3" />{dl.length}</span>}
        {sc.length > 0 && <span className="px-1.5 h-5 inline-flex items-center gap-0.5 rounded text-[11px] font-black text-white bg-sky-500" title="Jadwal produksi"><Wrench className="w-3 h-3" />{sc.length}</span>}
      </div>
      <div className="hidden sm:block space-y-0.5">
        {events.slice(0, 2).map((e) => <div key={e.kind + e.o.id} className={`truncate text-[11px] font-semibold ${toneOf(e, date, today).text}`}>{e.o.customer.name}</div>)}
        {events.length > 2 && <div className="text-[11px] text-slate-500">+{events.length - 2} lagi</div>}
      </div>
    </button>
  );
}

function DayOrder({ e, date, today }) {
  const t = toneOf(e, date, today);
  const { o } = e;
  return (
    <Link to={`/order/${o.id}`} data-testid={`cal-order-${o.order_no}`} className={`block p-3 rounded-xl border-2 ${t.card}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono font-bold text-sm">{o.order_no}</span>
        <span className={`text-[11px] font-black uppercase px-2 py-0.5 rounded text-white ${t.badge}`}>{t.label}</span>
      </div>
      <div className="font-extrabold text-slate-900 mt-1">{o.customer.name}</div>
      <div className="text-sm text-slate-600">{[...new Set(o.items.map((i) => `${i.cup_type} ${i.cup_size}`))].join(", ")} · {num(o.total_qty)} pcs</div>
      <div className="mt-2 flex items-center justify-between gap-2 text-xs text-slate-600"><span>{o.operator_name || "Belum ditugaskan"}{o.priority !== "Normal" && ` · ${o.priority}`}</span><StatusBadge status={o.status} /></div>
    </Link>
  );
}

function DayList({ date, events, today }) {
  return (
    <Card className="space-y-3" data-testid="cal-day-list">
      <h3 className="font-bold text-lg">{fmtDate(date)} <span className="text-slate-400 font-normal text-sm">· {events.length} order</span></h3>
      {events.length === 0 && <p className="text-sm text-slate-500">Tidak ada deadline atau jadwal produksi.</p>}
      {events.map((e) => <DayOrder key={e.kind + e.o.id} e={e} date={date} today={today} />)}
    </Card>
  );
}

export default function Calendar() {
  const today = todayWIB();
  const [ym, setYm] = useState(today.slice(0, 7));
  const [sel, setSel] = useState(today);
  const { data } = useApi(`/orders/calendar?month=${ym}`);
  if (!data) return <Loading />;
  const byDate = {};
  for (const o of data) {
    if (o.deadline?.startsWith(ym)) (byDate[o.deadline] ||= []).push({ kind: "deadline", o });
    if (o.schedule_date?.startsWith(ym)) (byDate[o.schedule_date] ||= []).push({ kind: "schedule", o });
  }
  const [y, m] = ym.split("-").map(Number);
  return (
    <>
      <PageHead title="Kalender Order" sub="Deadline & jadwal produksi per tanggal" />
      <div className="flex items-center justify-between gap-2">
        <Btn variant="secondary" className="!px-3" onClick={() => setYm(shiftMonth(ym, -1))} data-testid="cal-prev-btn" aria-label="Bulan sebelumnya"><ChevronLeft className="w-5 h-5" /></Btn>
        <div className="text-center">
          <div className="text-xl sm:text-2xl font-extrabold" data-testid="cal-month-label">{MONTHS[m - 1]} {y}</div>
          {ym !== today.slice(0, 7) && <button className="text-xs font-bold text-sky-600 underline" onClick={() => { setYm(today.slice(0, 7)); setSel(today); }} data-testid="cal-today-btn">Kembali ke bulan ini</button>}
        </div>
        <Btn variant="secondary" className="!px-3" onClick={() => setYm(shiftMonth(ym, 1))} data-testid="cal-next-btn" aria-label="Bulan berikutnya"><ChevronRight className="w-5 h-5" /></Btn>
      </div>
      <div className="flex flex-wrap gap-3 text-xs font-semibold text-slate-600" data-testid="cal-legend">
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-500" /> Deadline</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-rose-600" /> Deadline telat</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-sky-500" /> Jadwal produksi</span>
      </div>
      <div className="grid lg:grid-cols-12 gap-4">
        <div className="lg:col-span-8 rounded-2xl overflow-hidden border border-slate-200 border-t-0 border-l-0 shadow-sm" data-testid="cal-grid">
          <div className="grid grid-cols-7 bg-slate-900 text-white text-[11px] font-bold uppercase tracking-wide">{DAYS.map((d) => <div key={d} className="py-2 text-center">{d}</div>)}</div>
          <div className="grid grid-cols-7">
            {buildGrid(ym).map((date, i) => <DayCell key={date || `e${i}`} date={date} events={byDate[date] || []} today={today} selected={sel} onSelect={setSel} />)}
          </div>
        </div>
        <div className="lg:col-span-4"><DayList date={sel} events={byDate[sel] || []} today={today} /></div>
      </div>
    </>
  );
}
